import React, { useEffect, useState } from "react";
import {
  TextField,
  Button,
  Box,
  FormControlLabel,
  Switch,
} from "@mui/material";
import CommonModal from "../common/CommonModal";
import { useTranslation } from "react-i18next";
import { ApplicationPackage, ApplicationPackageRequest } from "../../hooks/useApplicationPackages";

interface PackageFormModalProps {
  open: boolean;
  handleClose: () => void;
  onSubmit: (formData: ApplicationPackageRequest & { id?: number }) => void;
  isSubmitting?: boolean;
  isEdit?: boolean;
  initialData?: ApplicationPackage;
}

const PackageFormModal: React.FC<PackageFormModalProps> = ({
  open,
  handleClose,
  onSubmit,
  isSubmitting = false,
  isEdit = false,
  initialData,
}) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<ApplicationPackageRequest & { id?: number }>({
    name: "",
    description: "",
    active: true,
    price: 0,
  });

  useEffect(() => {
    if (open) {
      if (isEdit && initialData) {
        setFormData({
          id: initialData.id,
          name: initialData.name || "",
          description: initialData.description || "",
          active: initialData.active ?? true,
          price: initialData.price ?? 0,
        });
      } else {
        setFormData({ name: "", description: "", active: true, price: 0 });
      }
    }
  }, [open, isEdit, initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...(isEdit && formData.id != null ? { id: formData.id } : {}),
      name: formData.name.trim(),
      description: formData.description?.trim() || "",
      active: formData.active ?? true,
      price: Number(formData.price) || 0,
    });
  };

  const isValid = !!formData.name.trim();

  return (
    <CommonModal
      open={open}
      onClose={handleClose}
      title={isEdit ? t("edit_package") : t("add_package")}
      maxWidth="sm"
      customActions={
        <>
          <Button
            onClick={handleClose}
            variant="outlined"
            disabled={isSubmitting}
            sx={{ borderColor: "#3092e3", color: "#3092e3", "&:hover": { borderColor: "#1a6fb5", color: "#1a6fb5", backgroundColor: "rgba(48,146,227,0.08)" } }}
          >
            {t("cancel")}
          </Button>
          <Button
            form="package-form"
            type="submit"
            variant="contained"
            disabled={!isValid || isSubmitting}
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
          >
            {isSubmitting ? t("saving") : isEdit ? t("update") : t("add")}
          </Button>
        </>
      }
    >
      <form id="package-form" onSubmit={handleSubmit}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label={t("package_name")}
              value={formData.name}
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              required
              fullWidth
            />
            <TextField
              label={t("description")}
              value={formData.description}
              onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label={t("price")}
              type="number"
              value={formData.price ?? ""}
              onChange={(e) => setFormData((prev) => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
              fullWidth
              inputProps={{ min: 0, step: "0.01" }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.active ?? true}
                  onChange={(e) => setFormData((prev) => ({ ...prev, active: e.target.checked }))}
                />
              }
              label={t("active")}
            />
          </Box>
      </form>
    </CommonModal>
  );
};

export default PackageFormModal;
